import React, { useState, useEffect, useRef } from "react";
import "./terminal.css";
import projectsData from "../../data/projectsData";

const Terminal = ({ onSwitchMode }) => {
  const [output, setOutput] = useState([
    { type: "text", content: "Welcome to Dev Terminal 🚀" },
    { type: "text", content: 'Type "help" to see all available commands' },
    { type: "text", content: "" },
  ]);
  const [input, setInput] = useState("");
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [suggestions, setSuggestions] = useState([]);
  const terminalEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    terminalEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [output]);

  const commands = {
    help: {
      description: "Display all available commands",
      execute: () => {
        const helpText = `
Available Commands:
───────────────────────────────────────────────────
help                         - Show this help message
whoami                       - Display your profile information
ls projects                  - List all 11 projects
ls projects --filter=ai      - Filter projects by AI/ML category
ls projects --filter=web     - Filter projects by Web App category
cat project <name>           - View detailed project information
open <project_name>          - Open project in browser
skills                       - Display technical skills
contact                      - Show contact information
social                       - Display social media links
blog                         - Open blog in browser
clear                        - Clear the terminal
exit                         - Switch to UI mode
───────────────────────────────────────────────────`;
        return [{ type: "text", content: helpText }];
      },
    },

    whoami: {
      description: "Display profile information",
      execute: () => {
        const profile = projectsData.profile;
        return [
          { type: "text", content: `Name: ${profile.name}` },
          { type: "text", content: `Title: ${profile.title}` },
          { type: "text", content: `Bio: ${profile.bio}` },
        ];
      },
    },

    clear: {
      description: "Clear the terminal",
      execute: () => {
        return "CLEAR_COMMAND";
      },
    },

    skills: {
      description: "Display technical skills",
      execute: () => {
        const skills = projectsData.skills;
        return [
          {
            type: "text",
            content: "Languages: " + skills.languages.join(", "),
          },
          { type: "text", content: "Frontend: " + skills.frontend.join(", ") },
          { type: "text", content: "Backend: " + skills.backend.join(", ") },
          { type: "text", content: "AI/ML: " + skills.aiml.join(", ") },
          { type: "text", content: "Tools: " + skills.tools.join(", ") },
        ];
      },
    },

    contact: {
      description: "Display contact information",
      execute: () => {
        const contact = projectsData.contact;
        return [
          { type: "text", content: `Email: ${contact.email}` },
          { type: "text", content: `Location: ${contact.location}` },
          { type: "text", content: `Website: ${contact.website}` },
        ];
      },
    },

    social: {
      description: "Display social media links",
      execute: () => {
        const social = projectsData.social;
        return [
          { type: "text", content: `GitHub: ${social.github}` },
          { type: "text", content: `LinkedIn: ${social.linkedin}` },
          { type: "text", content: `Twitter: ${social.twitter}` },
          { type: "text", content: `Email: ${social.email}` },
        ];
      },
    },

    blog: {
      description: "Open blog in browser",
      execute: () => {
        window.open("https://blog.sbdev.me", "_blank");
        return [{ type: "text", content: "Opening blog..." }];
      },
    },
  };

  const executeCommand = (cmd) => {
    const trimmedCmd = cmd.trim().toLowerCase();
    const parts = trimmedCmd.split(" ");
    const baseCommand = parts[0];

    let result = [];

    if (baseCommand === "exit") {
      onSwitchMode("ui");
      return [];
    }

    if (baseCommand === "ls" && parts[1] === "projects") {
      if (parts[2] && parts[2].startsWith("--filter=")) {
        const filter = parts[2].split("=")[1].toLowerCase();
        const filtered = projectsData.projects.filter((p) =>
          p.category.toLowerCase().includes(filter),
        );
        if (filtered.length === 0) {
          return [
            {
              type: "text",
              content: `No projects found with filter: ${filter}`,
            },
          ];
        }
        result.push({
          type: "text",
          content: `Projects (${filter.toUpperCase()}):`,
        });
        filtered.forEach((p) => {
          result.push({
            type: "text",
            content: `  ${p.name.padEnd(20)} - ${p.title}`,
          });
        });
      } else {
        result.push({ type: "text", content: "All Projects:" });
        projectsData.projects.forEach((p) => {
          result.push({
            type: "text",
            content: `  ${p.name.padEnd(20)} - ${p.title} [${p.category}]`,
          });
        });
      }
      return result;
    }

    if (baseCommand === "cat" && parts[1] === "project") {
      const projectName = parts[2];
      if (!projectName) {
        return [{ type: "text", content: "Usage: cat project <project_name>" }];
      }
      const project = projectsData.projects.find(
        (p) => p.name.toLowerCase() === projectName.toLowerCase(),
      );
      if (!project) {
        return [{ type: "text", content: `Project not found: ${projectName}` }];
      }
      return [
        { type: "text", content: `Project: ${project.title}` },
        { type: "text", content: `Category: ${project.category}` },
        { type: "text", content: `Description: ${project.description}` },
        {
          type: "text",
          content: `Technologies: ${project.technologies.join(", ")}`,
        },
        { type: "text", content: `Live: ${project.liveLink}` },
        { type: "text", content: `GitHub: ${project.github}` },
      ];
    }

    if (baseCommand === "open") {
      const projectName = parts[1];
      if (!projectName) {
        return [{ type: "text", content: "Usage: open <project_name>" }];
      }
      const project = projectsData.projects.find(
        (p) => p.name.toLowerCase() === projectName.toLowerCase(),
      );
      if (!project) {
        return [{ type: "text", content: `Project not found: ${projectName}` }];
      }
      window.open(project.liveLink, "_blank");
      return [{ type: "text", content: `Opening ${project.title}...` }];
    }

    if (commands[baseCommand]) {
      return commands[baseCommand].execute();
    }

    return [
      {
        type: "text",
        content: `Command not found: ${baseCommand}. Type "help" for available commands.`,
      },
    ];
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (!input.trim()) return;

      const trimmedCmd = input.trim().toLowerCase();

      // Handle clear command specially
      if (trimmedCmd === "clear") {
        setOutput([]);
        setHistory([...history, input]);
        setHistoryIndex(-1);
        setInput("");
        setSuggestions([]);
        return;
      }

      const newOutput = [
        ...output,
        { type: "command", content: input },
        ...executeCommand(input),
      ];

      setOutput(newOutput);
      setHistory([...history, input]);
      setHistoryIndex(-1);
      setInput("");
      setSuggestions([]);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      const newIndex = historyIndex + 1;
      if (newIndex < history.length) {
        setHistoryIndex(newIndex);
        setInput(history[history.length - 1 - newIndex]);
      }
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      if (historyIndex > 0) {
        const newIndex = historyIndex - 1;
        setHistoryIndex(newIndex);
        setInput(history[history.length - 1 - newIndex]);
      } else if (historyIndex === 0) {
        setHistoryIndex(-1);
        setInput("");
      }
    } else if (e.key === "Tab") {
      e.preventDefault();
      const trimmedInput = input.trim().toLowerCase();
      const allCommands = [
        ...Object.keys(commands),
        "ls",
        "cat",
        "open",
        "exit",
      ];
      const matches = allCommands.filter((cmd) => cmd.startsWith(trimmedInput));
      if (matches.length === 1) {
        setInput(matches[0] + " ");
      } else if (matches.length > 1) {
        setSuggestions(matches);
      }
    }
  };

  return (
    <div className="terminal-container">
      <div className="terminal-header">
        <div className="terminal-title">Dev Terminal</div>
        <button
          className="terminal-switch-btn"
          onClick={() => onSwitchMode("ui")}
        >
          UI Mode
        </button>
      </div>

      <div className="terminal-content">
        {output.map((line, idx) => (
          <div key={idx} className={`terminal-line terminal-${line.type}`}>
            {line.type === "command" ? (
              <>
                <span className="terminal-prompt">➜</span>
                <span className="terminal-command">{line.content}</span>
              </>
            ) : (
              <span className="terminal-text">{line.content}</span>
            )}
          </div>
        ))}
        <div ref={terminalEndRef} />
      </div>

      {suggestions.length > 0 && (
        <div className="terminal-suggestions">
          <span>Suggestions: {suggestions.join(", ")}</span>
        </div>
      )}

      <div className="terminal-input-wrapper">
        <span className="terminal-prompt">➜</span>
        <input
          ref={inputRef}
          type="text"
          className="terminal-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          autoFocus
          placeholder="Type command... (help for commands)"
        />
      </div>
    </div>
  );
};

export default Terminal;
